import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Download, FileUp, Loader2, Send, Share2, X } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  {
    id: "jrf-data-signal",
    num: "R.01",
    title: "Intern - Multimodal Data and Signal Processing",
    team: "Research",
    location: "Guwahati · Hybrid",
    type: "Internship",
    duration: "Full-time · three-month research deliverables",
    stipend: "Stipend + up to 30% incentive",
    eligibility: "B.E./B.Tech., M.E./M.Tech. or M.Sc. in a relevant discipline",
    jdUrl: "/assets/jd/intern-multimodal-data-signal-processing.pdf",
    summary:
      "Help us turn raw biosignals into reliable, well-versioned datasets: ingestion, cleaning, and analysis pipelines for biosignals.",
    responsibilities: [
      "Build and own pipelines that ingest, clean, and version physiological signal data.",
      "Design experiments and evaluation harnesses with our academic and clinical research partners.",
      "Prototype models that fuse multimodal signals into interpretable indicators of cognitive state.",
      "Write it up: internal memos, reproducible notebooks, and documentation a clinician could audit.",
    ],
    youBring: [
      "Strong Python and signal-processing fundamentals (filtering, spectral analysis, artefact handling).",
      "Hands-on ML experience with honest, reproducible evaluation.",
      "Comfort working shoulder-to-shoulder with scientists; curiosity about cognition is a must.",
    ],
  },
  {
    id: "jrf-ml-prototypes",
    num: "R.02",
    title: "Intern - ML Engineering and Prototype Integration",
    team: "Research",
    location: "Guwahati · Hybrid",
    type: "Internship",
    duration: "Full-time · three-month research & engineering deliverables",
    stipend: "Stipend + up to 30% incentive",
    eligibility: "B.E./B.Tech. (with relevant experience), M.E./M.Tech. or M.Sc. in a relevant discipline",
    jdUrl: "/assets/jd/intern-ml-engineering-prototype-integration.pdf",
    summary:
      "Train and evaluate the multimodal models that interpret cognitive and physiological signals, and wire them into working prototypes the research team can actually use.",
    responsibilities: [
      "Train, evaluate, and iterate on multimodal models for cognitive-state estimation.",
      "Integrate models into prototypes and internal tooling used by the research team.",
      "Design honest evaluation harnesses with our academic and clinical partners.",
      "Document experiments so results are reproducible end to end.",
    ],
    youBring: [
      "Solid Python and ML fundamentals (PyTorch or similar).",
      "Experience taking models from notebook to working prototype.",
      "Curiosity about cognition and comfort working alongside scientists.",
    ],
  },
];

const WORK_PILLARS = [
  {
    name: "Exploratory",
    body: "Working on questions where the answers are not yet established.",
    practice:
      "In practice, this means forming hypotheses without an existing playbook to follow, running experiments that fail as often as they succeed, and treating each dead end as information rather than a setback.",
    traits: [
      ["Curious", "naturally driven to ask why, what if, and what else."],
      ["Open-minded", "comfortable changing direction when new evidence challenges an assumption."],
      ["Persistent", "willing to stay with ambiguity and uncertainty longer than most."],
    ],
  },
  {
    name: "Scientific",
    body: "Grounding discovery in evidence, experimentation and rigorous inquiry.",
    practice:
      "In practice, this means testing claims before trusting them, documenting method as carefully as result, and being ready to set aside a promising idea the moment the evidence stops supporting it.",
    traits: [
      ["Rigorous", "unwilling to accept convenient answers without scrutiny."],
      ["Intellectually honest", "comfortable saying \"we don't know yet.\""],
      ["Patient", "willing to test, learn and refine rather than rush to conclusions."],
    ],
  },
  {
    name: "Interdisciplinary",
    body: "Bringing neuroscience, neurotechnology, AI and engineering into the same problem space.",
    practice:
      "In practice, this means working alongside people trained very differently from you, translating between disciplines, and accepting that the best answer to a problem may come from a field you don't specialise in.",
    traits: [
      ["Collaborative", "genuinely values what others bring to a problem."],
      ["Humble", "willing to learn from disciplines outside one's own."],
      ["Adaptable", "able to move between different ways of thinking."],
    ],
  },
  {
    name: "Translational",
    body: "Moving from scientific understanding toward technologies that can work beyond the laboratory.",
    practice:
      "In practice, this means taking a result that works under controlled conditions and reworking it until it holds up in the real world, often with fewer resources and less certainty than the lab allowed.",
    traits: [
      ["Pragmatic", "keeps sight of what can actually work in the real world."],
      ["Resourceful", "finds ways forward when the obvious path doesn't exist."],
      ["Action-oriented", "turns understanding into experiments, prototypes and progress."],
    ],
  },
];

const WE_GIVE = [
  {
    title: "No micro-management",
    body: "We value and promote a genuine sense of agency. Once the scope of a problem is clear, how you approach it is largely yours to decide. Direction comes from working through open questions together, not from oversight.",
  },
  {
    title: "Supporting and collaborative systems",
    body: "We do our best to make full use of your skills, and to help you build new ones, without letting you lose your self-belief along the way.",
  },
  {
    title: "Belief and trust",
    body: "We extend trust early rather than ask you to earn it over years. That trust comes with real responsibility, and we mean it as more than a phrase.",
  },
  {
    title: "Fair compensation",
    body: "We are bootstrapped, so we may not always match what the market can offer. We will always aim to make our compensation fair, meaningful and weighted toward what you actually take home.",
  },
];

const WE_LOOK = [
  ["Question deeply.", "Instinctively pushes past the first explanation offered, unwilling to stop at whatever answer is easiest to accept. This keeps investigation alive even when a surface explanation would be simpler to settle for."],
  ["Think rigorously.", "Regularly examines their own reasoning, evidence and assumptions rather than assuming the first read is correct. This habit of self-scrutiny catches errors before they get built into the next step."],
  ["Act deliberately.", "Turns thought and ideation into intentional action rather than letting analysis substitute for progress. Ideas are treated as a starting point for doing something, not an end in themselves."],
  ["Reflect honestly.", "Turns experience into learning by examining what actually happened, including the parts that did not go well. Honest reflection is treated as more valuable than a comfortable retelling."],
  ["Challenge assumptions.", "Stays willing to revise their own beliefs and models of action when the evidence demands it. Holding on to an assumption past its useful life is treated as a liability, not consistency."],
  ["Find a way forward.", "Converts uncertainty, failure and constraint into adaptation rather than letting them stall progress. Setbacks are treated as information to work with, not reasons to stop."],
];

const SectionHead = ({ num, title, body, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
    className="mb-12"
    data-testid={testId}
  >
    <div className="flex items-center gap-5">
      <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-[#10B8B1]">{num}</span>
      <span className="flex-1 h-px bg-white/10" />
    </div>
    <h2 className="mt-6 font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-100">{title}</h2>
    {body && <p className="mt-4 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">{body}</p>}
  </motion.div>
);

const SpinBadge = () => (
  <div className="relative w-28 h-28 md:w-32 md:h-32" data-testid="careers-spin-badge" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_18s_linear_infinite]">
      <defs>
        <path id="qelp-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
      </defs>
      <text fontSize="9" letterSpacing="2.6" fill="#10B8B1" fontFamily="'JetBrains Mono', monospace">
        <textPath href="#qelp-circle">WE'RE HIRING · JOIN THE LAB ·</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="w-2 h-2 rounded-full bg-[#10B8B1] animate-qelp-pulse" />
    </span>
  </div>
);

const WhatsAppIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const RolesSnapshot = ({ onRead }) => {
  const [peek, setPeek] = useState(null);
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 pb-16 md:pb-20" data-testid="careers-roles-snapshot">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center gap-5 mb-8">
          <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-[#10B8B1]">01</span>
          <span className="flex-1 h-px bg-white/10" />
          <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-slate-400">Open Roles at a Glance</span>
        </div>
        <div className="grid md:grid-cols-2 gap-5 items-start">
          {ROLES.map((r) => {
            const open = peek === r.id;
            return (
              <div
                key={r.id}
                className="qelp-card rounded-3xl p-6 md:p-7 flex flex-col gap-4"
                data-testid={`snapshot-${r.id}`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-mono-qelp text-[10px] tracking-[0.25em] text-[#10B8B1]">{r.num}</span>
                  <span className="font-mono-qelp text-[9px] tracking-[0.18em] uppercase text-slate-400">{r.location} · {r.type}</span>
                </div>
                <h3 className="font-heading text-lg sm:text-xl font-medium text-slate-100 leading-snug min-h-14 flex items-start">{r.title}</h3>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-white/10 pt-4 space-y-4" data-testid={`snapshot-preview-${r.id}`}>
                        <p className="text-sm text-slate-300 leading-relaxed">{r.summary}</p>
                        <div>
                          <p className="font-mono-qelp text-[10px] tracking-[0.22em] uppercase text-[#10B8B1] mb-2.5">What you'll do</p>
                          <ul className="space-y-2">
                            {r.responsibilities.map((item, i) => (
                              <li key={i} className="text-sm text-slate-300 leading-relaxed flex gap-2.5">
                                <span className="text-[#10B8B1] mt-px">•</span>{item}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="font-mono-qelp text-[10px] tracking-[0.22em] uppercase text-[#10B8B1] mb-2.5">What you'll bring</p>
                          <ul className="space-y-2">
                            {r.youBring.map((item, i) => (
                              <li key={i} className="text-sm text-slate-300 leading-relaxed flex gap-2.5">
                                <span className="text-[#10B8B1] mt-px">•</span>{item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center gap-6 mt-auto pt-1">
                  <button
                    onClick={() => setPeek(open ? null : r.id)}
                    className="inline-flex items-center gap-2 font-mono-qelp text-[11px] tracking-[0.18em] uppercase text-slate-300 hover:text-[#10B8B1] transition-colors duration-300"
                    data-testid={`snapshot-peek-${r.id}`}
                    aria-expanded={open}
                  >
                    {open ? "Close Preview" : "Peek Preview"}
                    <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                  </button>
                  <button
                    onClick={() => onRead(r)}
                    className="inline-flex items-center gap-2 font-mono-qelp text-[11px] tracking-[0.18em] uppercase text-slate-300 hover:text-[#10B8B1] transition-colors duration-300"
                    data-testid={`snapshot-details-${r.id}`}
                  >
                    Role Details <span aria-hidden="true">→</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

const RoleCard = ({ role, onApply, open, onToggle }) => {
  const share = async () => {
    const url = `${window.location.origin}/careers#${role.id}`;
    const payload = {
      title: `Qelp AI: ${role.title}`,
      text: `Qelp AI is hiring: ${role.title} · ${role.location} · ${role.type}`,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(payload); } catch { /* dismissed */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Role link copied. Pass it on.");
      } catch {
        toast.error("Couldn't copy the link. Please copy the page URL instead.");
      }
    }
  };

  return (
    <motion.article
      id={role.id}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
      className="qelp-card rounded-3xl p-7 md:p-10 border-t border-white/10 scroll-mt-24"
      data-testid={`role-card-${role.id}`}
    >
      <div className="grid md:grid-cols-12 gap-6 items-start">
        <div className="md:col-span-2">
          <span className="font-heading text-4xl md:text-5xl leading-none text-transparent [-webkit-text-stroke:1px_rgba(16,184,177,0.5)]">
            {role.num}
          </span>
        </div>
        <div className="md:col-span-10">
          <div className="flex flex-wrap gap-2 mb-4">
            {[role.team, role.location, role.type].map((t) => (
              <span key={t} className="rounded-full border border-[#10B8B1]/25 px-3.5 py-1 font-mono-qelp text-[9px] tracking-[0.18em] uppercase text-slate-400">
                {t}
              </span>
            ))}
          </div>
          <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-slate-100">
            {role.title}
          </h3>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-2xl">{role.summary}</p>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="grid sm:grid-cols-2 gap-8 pt-7 mt-7 border-t border-white/10" data-testid={`role-jd-${role.id}`}>
                  <div>
                    <p className="font-mono-qelp text-[10px] tracking-[0.25em] uppercase text-[#10B8B1] mb-4">What you'll do</p>
                    <ul className="space-y-3">
                      {role.responsibilities.map((r, i) => (
                        <li key={i} className="text-sm text-slate-400 leading-relaxed flex gap-3">
                          <span className="text-[#10B8B1] mt-1">•</span>{r}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-mono-qelp text-[10px] tracking-[0.25em] uppercase text-[#10B8B1] mb-4">What you'll bring</p>
                    <ul className="space-y-3">
                      {role.youBring.map((r, i) => (
                        <li key={i} className="text-sm text-slate-400 leading-relaxed flex gap-3">
                          <span className="text-[#10B8B1] mt-1">•</span>{r}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-5 pt-6 border-t border-white/5" data-testid={`role-terms-${role.id}`}>
                    {[
                      ["Duration", role.duration],
                      ["Stipend", role.stipend],
                      ["Eligibility", role.eligibility],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p className="font-mono-qelp text-[9px] tracking-[0.22em] uppercase text-slate-500">{label}</p>
                        <p className="mt-1.5 text-sm text-slate-300">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            <button
              onClick={() => onApply(role)}
              className="qelp-btn-primary rounded-full px-6 py-2.5 font-heading font-semibold text-sm"
              data-testid={`role-apply-${role.id}`}
            >
              Apply for this role
            </button>
            {role.jdUrl && (
              <a
                href={role.jdUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#10B8B1]/25 px-5 py-2.5 font-mono-qelp text-[10px] tracking-[0.18em] uppercase text-slate-300 hover:border-[#10B8B1] hover:text-[#10B8B1] transition-all duration-300"
                data-testid={`role-jd-download-${role.id}`}
              >
                <Download size={13} /> Download the JD
              </a>
            )}
            <button
              onClick={onToggle}
              className="inline-flex items-center gap-2 font-mono-qelp text-[11px] tracking-[0.18em] uppercase text-slate-400 hover:text-[#10B8B1] transition-colors duration-300"
              data-testid={`role-toggle-${role.id}`}
            >
              {open ? "Close the role" : "Read the role"}
              <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <button
              onClick={share}
              className="inline-flex items-center gap-2 font-mono-qelp text-[11px] tracking-[0.18em] uppercase text-slate-400 hover:text-[#10B8B1] transition-colors duration-300"
              data-testid={`role-share-${role.id}`}
              aria-label={`Share ${role.title}`}
            >
              <Share2 size={14} /> Share
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Qelp AI is hiring: ${role.title} · ${role.location} · ${role.type} · ${window.location.origin}/careers#${role.id}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-mono-qelp text-[11px] tracking-[0.18em] uppercase text-slate-400 hover:text-[#10B8B1] transition-colors duration-300"
              data-testid={`role-whatsapp-${role.id}`}
              aria-label={`Share ${role.title} on WhatsApp`}
            >
              <WhatsAppIcon /> WhatsApp
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const Careers = () => {
  const [form, setForm] = useState({ name: "", email: "", portfolio: "", message: "", website: "" });
  const [role, setRole] = useState(ROLES[0].title);
  const [cv, setCv] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const formRef = useRef(null);
  const [openRole, setOpenRole] = useState(null);

  const scrollToEl = (el) => {
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -90, duration: 1.2 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const readRole = (r) => {
    setOpenRole(r.id);
    scrollToEl(document.getElementById(r.id));
  };

  useEffect(() => {
    const prevTitle = document.title;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : "";
    document.title = "Careers at Qelp AI";
    if (desc) {
      desc.setAttribute("content", "Careers at Qelp AI: open intern roles in multimodal data and signal processing and ML engineering. Guwahati, hybrid.");
    }
    return () => {
      document.title = prevTitle;
      if (desc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyFor = (r) => {
    setRole(r.title);
    scrollToEl(formRef.current);
  };

  const pickFile = (file) => {
    if (!file) return;
    const ok = ["pdf", "doc", "docx"].includes(file.name.split(".").pop().toLowerCase());
    if (!ok) return toast.error("CV must be a PDF, DOC or DOCX file.");
    if (file.size > 5 * 1024 * 1024) return toast.error("CV must be under 5MB.");
    setCv(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.portfolio || !cv) {
      return toast.error("Name, email, portfolio link and CV are required.");
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("role", role);
      data.append("cv", cv);
      await axios.post(`${API}/careers/apply`, data);
      toast.success("Application received. Check your inbox for a confirmation.");
      setForm({ name: "", email: "", portfolio: "", message: "", website: "" });
      setCv(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="qelp-grain min-h-screen bg-[#050422] text-slate-50" data-testid="careers-page">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono-qelp text-xs tracking-[0.3em] uppercase text-[#10B8B1]" data-testid="careers-overline">Careers</p>
            <h1 className="mt-6 font-heading font-medium tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-[clamp(2.5rem,4.5vw,3.75rem)] text-slate-50" data-testid="careers-heading">
              Build what comes next
              <span className="block">in <span className="italic text-[#10B8B1]">cognitive intelligence.</span></span>
            </h1>
            <p className="mt-6 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed" data-testid="careers-intro">
              Qelp AI is a neurotechnology research team in Guwahati, India. We are working to measure human cognitive state in real time and under real-world conditions, a problem that is still unsolved. If you want to spend your early career on questions without established answers, explore our open roles below.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
            className="lg:col-span-5 relative mt-6 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none"
            data-testid="careers-hero-visual"
          >
            <div className="qelp-card rounded-3xl overflow-hidden">
              <img src="/assets/mri/careers-scan.webp" alt="Axial T1 MRI slice through the thalamus and basal ganglia, ICBM152 reference volume" className="w-full h-auto opacity-90" />
              <div className="flex items-center justify-between px-5 py-3 border-t border-white/10">
                <span className="font-mono-qelp text-[9px] tracking-[0.22em] uppercase text-slate-500">MNI152 · Axial</span>
                <span className="font-mono-qelp text-[9px] tracking-[0.22em] uppercase text-[#10B8B1]">T1 · ICBM152</span>
              </div>
            </div>
            <div className="absolute -top-7 -right-3 md:-right-6">
              <SpinBadge />
            </div>
          </motion.div>
        </div>
      </section>

      <RolesSnapshot onRead={readRole} />

      <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20" data-testid="careers-work">
        <SectionHead
          num="02"
          title="The Work"
          body="The way we work is defined less by process than by disposition. Four dimensions describe it: what each one means, what it looks like in practice, and what it takes to thrive in it."
          testId="careers-work-head"
        />
        <div className="space-y-6">
          {WORK_PILLARS.map((p) => (
            <motion.article
              key={p.name}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="qelp-card rounded-3xl p-7 md:p-10"
              data-testid={`work-${p.name.toLowerCase()}`}
            >
              <h3 className="font-heading italic text-2xl sm:text-3xl text-[#10B8B1]">{p.name}</h3>
              <p className="mt-3 max-w-2xl text-sm sm:text-base text-slate-300 leading-relaxed">{p.body}</p>

              <div className="mt-8 pt-8 border-t border-white/10" data-testid={`work-practice-${p.name.toLowerCase()}`}>
                <p className="font-mono-qelp text-[10px] tracking-[0.25em] uppercase text-[#10B8B1]">In practice</p>
                <p className="mt-4 max-w-3xl text-sm text-slate-400 leading-relaxed">{p.practice}</p>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10" data-testid={`work-traits-${p.name.toLowerCase()}`}>
                <p className="font-mono-qelp text-[10px] tracking-[0.25em] uppercase text-[#10B8B1]">What it takes to thrive</p>
                <div className="mt-6 grid sm:grid-cols-3 gap-6">
                  {p.traits.map(([t, d]) => (
                    <div key={t}>
                      <p className="font-mono-qelp text-[11px] tracking-[0.18em] uppercase text-slate-200">{t}</p>
                      <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20" data-testid="careers-give">
        <SectionHead
          num="03"
          title="What We Can Give"
          body="What we offer is not the usual list of perks. It is a specific way of working, built around agency, trust, and support that is meant to hold up under real pressure."
          testId="careers-give-head"
        />
        <div className="grid sm:grid-cols-2 gap-5">
          {WE_GIVE.map((g, i) => (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: (i % 2) * 0.08 }}
              className="qelp-card rounded-3xl p-7"
              data-testid={`give-${i}`}
            >
              <span className="font-heading text-3xl leading-none text-transparent [-webkit-text-stroke:1px_rgba(16,184,177,0.5)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-4 font-heading text-lg sm:text-xl font-semibold text-white">{g.title}</h3>
              <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{g.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20" data-testid="careers-look">
        <SectionHead
          num="04"
          title="What We Look For"
          body="Beyond technical fit, these are the working instincts we look for in anyone joining the lab."
          testId="careers-look-head"
        />
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
          {WE_LOOK.map(([title, body], i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (i % 2) * 0.08 }}
              className="flex gap-5 border-t border-white/10 pt-6"
              data-testid={`look-${i}`}
            >
              <span className="font-mono-qelp text-[10px] tracking-[0.2em] text-[#10B8B1] pt-1.5 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-100">{title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 py-16 md:py-20" data-testid="careers-roles">
        <SectionHead
          num="05"
          title="Open Roles"
          body="Two intern positions are open. Full terms for both roles (duration, stipend and eligibility) are confirmed in the downloadable JDs on each card."
          testId="careers-roles-head"
        />
        <div className="space-y-6">
          {ROLES.map((r) => (
            <RoleCard
              key={r.id}
              role={r}
              onApply={applyFor}
              open={openRole === r.id}
              onToggle={() => setOpenRole(openRole === r.id ? null : r.id)}
            />
          ))}
        </div>
      </section>

      <section ref={formRef} className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24 pt-14 pb-24" data-testid="careers-apply">
        <div className="flex items-center gap-5 mb-8">
          <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-[#10B8B1]">Apply</span>
          <span className="flex-1 h-px bg-white/10" />
          <span className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-slate-400">2 minutes, no cover letter theatre</span>
        </div>

        <form onSubmit={submit} className="qelp-card rounded-3xl p-7 md:p-10 space-y-6" data-testid="careers-form">
          <div>
            <label className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Role</label>
            <div className="flex flex-wrap gap-2.5">
              {ROLES.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setRole(r.title)}
                  className={`rounded-full px-4 py-2 font-mono-qelp text-[10px] tracking-[0.12em] uppercase transition-all duration-300 ${
                    role === r.title
                      ? "bg-[#10B8B1] text-[#050422]"
                      : "border border-[#10B8B1]/25 text-slate-400 hover:text-[#10B8B1]"
                  }`}
                  data-testid={`careers-role-pick-${r.id}`}
                >
                  {r.num}
                </button>
              ))}
              <span className="self-center font-mono-qelp text-[11px] tracking-[0.1em] text-slate-300 ml-1" data-testid="careers-role-label">{role}</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="car-name" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Full Name *</label>
              <input id="car-name" className="qelp-input" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="careers-input-name" />
            </div>
            <div>
              <label htmlFor="car-email" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Email *</label>
              <input id="car-email" type="email" className="qelp-input" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="careers-input-email" />
            </div>
          </div>

          <div>
            <label htmlFor="car-portfolio" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Portfolio / GitHub / LinkedIn *</label>
            <input id="car-portfolio" className="qelp-input" placeholder="https://…" value={form.portfolio} onChange={set("portfolio")} data-testid="careers-input-portfolio" />
          </div>

          <div>
            <label className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">CV (PDF, DOC, DOCX · max 5MB) *</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-all duration-300 ${
                dragging ? "border-[#10B8B1] bg-[#10B8B1]/10 scale-[1.01]" : "border-[#10B8B1]/25 hover:border-[#10B8B1]/60 hover:bg-[#10B8B1]/5"
              }`}
              data-testid="careers-cv-dropzone"
            >
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => pickFile(e.target.files[0])} data-testid="careers-cv-input" />
              {cv ? (
                <div className="flex items-center justify-center gap-3">
                  <FileUp size={18} className="text-[#10B8B1]" />
                  <span className="text-sm text-slate-200">{cv.name}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setCv(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-500 hover:text-[#10B8B1]" aria-label="Remove CV" data-testid="careers-cv-remove">
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <>
                  <FileUp size={22} className="text-[#10B8B1] mx-auto" />
                  <p className="mt-3 text-sm text-slate-300">Drop your CV here, or <span className="text-[#10B8B1] underline underline-offset-4">browse</span></p>
                  <p className="mt-1 font-mono-qelp text-[10px] tracking-[0.15em] uppercase text-slate-400">We actually read these</p>
                </>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="car-message" className="block font-mono-qelp text-[11px] tracking-[0.2em] uppercase text-slate-300 mb-2.5">Anything we should know?</label>
            <textarea id="car-message" rows={4} className="qelp-input resize-none" placeholder="Optional: a project you're proud of, why cognition, your favourite artefact story…" value={form.message} onChange={set("message")} data-testid="careers-input-message" />
          </div>

          <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="car-website">Website</label>
            <input id="car-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="careers-honeypot" />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="qelp-btn-primary rounded-full px-8 py-3.5 font-heading font-semibold text-sm tracking-wide inline-flex items-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="careers-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending…" : "Send Application"}
          </button>
        </form>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
